import React from 'react';
import PropTypes from 'prop-types';
import startCase from 'lodash/startCase';
import { browserHistory } from 'react-router';
import { formatTimeFromSeconds, Roles } from '../../../../core/util';
import Table from '../../../../core/form/Table';
import Button from '../../../../core/form/Button';
import Tag from '../../../../core/layout/Tag';

const UnitList = (props) => (
  <Table
    fetching={props.fetching}
    rowClassName={(row) => (['DRAFT', 'UNPUBLISHED'].includes(row.status) ? 'table-row-dark' : 'table-row-light')}
    columns={[
      {
        label: 'Group',
        path: 'group',
        width: '75px',
        sort: true,
      },
      {
        label: 'Order',
        path: 'order',
        width: '75px',
        sort: true,
      },
      {
        label: 'Name',
        path: 'name',
        sort: true,
        render: (cell, row) => (
          <>
            <label>{cell}</label>
            {!!row.bncc?.length && (
              <>
                <br />
                <small>BNCC: {row.bncc.join(', ')}</small>
              </>
            )}
          </>
        ),
      },
      {
        label: 'Difficulty',
        path: 'difficulty',
        width: '85px',
        render: (cell, row) => `${cell} - ${row.scoreToPass}`,
      },
      {
        label: 'Abilities',
        path: 'type.abilities',
        width: '70px',
        render: (cell, row) => (
          <div>
            {row.type.abilities.map((ability, index) => {
              if (row.type.abilities.length !== index + 1) {
                return `${ability.charAt(0)}, `;
              }
              return `${ability.charAt(0)}`;
            })}
          </div>
        ),
      },
      {
        label: 'Unit Type',
        path: 'type.name',
        width: '135px',
        sort: true,
      },
      {
        label: 'Status',
        path: 'status',
        width: '100px',
        render: (cell) =>
          cell && (
            <Tag color={{ DRAFT: 'grey', PUBLISHED: 'green', UNPUBLISHED: 'red' }[cell]}>
              {startCase(cell.toLowerCase())}
            </Tag>
          ),
      },
      {
        label: 'Items count',
        path: 'itemsCount',
        width: '110px',
      },
      {
        label: 'Items with audio',
        path: 'itemsWithAudio',
        width: '120px',
        render: (cell, row) => (row.itemsThatNeedAudio ? `${cell}/${row.itemsThatNeedAudio}` : 'N/A'),
      },
      {
        label: 'Items with image',
        path: 'itemsWithImage',
        width: '120px',
        render: (cell, row) => (row.itemsThatNeedImage ? `${cell}/${row.itemsThatNeedImage}` : 'N/A'),
      },
      {
        label: 'Items with video',
        path: 'itemsWithVideo',
        width: '120px',
        render: (cell, row) => (row.itemsThatNeedVideo ? `${cell}/${row.itemsThatNeedVideo}` : 'N/A'),
      },
      {
        label: 'Unit time',
        path: 'time',
        width: '85px',
        render: (cell) => formatTimeFromSeconds(cell),
      },
      {
        label: 'Actions',
        path: 'action',
        width: '10%',
        render: (cell, row, index) => (
          <div>
            {(row.createdBy === localStorage.id || localStorage.role === Roles.ADMIN) &&
              row.status === 'DRAFT' &&
              !row.itemsCount && (
                <Button icon="delete" onClick={() => props.onDelete(row)} tooltip="Remove unit" />
              )}{' '}
            {(row.createdBy === localStorage.id ||
              [Roles.ADMIN, Roles.SUPPORT, Roles.DISTRIBUTOR_CONTENT_ADMIN].some((r) => r === localStorage.role)) && (
              <Button
                icon="edit"
                onClick={() => browserHistory.push(`/modules/${row.module}/units/${row.id}`)}
                tooltip="Edit info"
              />
            )}{' '}
            {props.allowReorder && <Button label="+1" onClick={() => props.onAutoReorder(index, 'ADD_LINE')} />}{' '}
            {props.allowReorder && <Button label="-1" onClick={() => props.onAutoReorder(index, 'REMOVE_LINE')} />}{' '}
            {[Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN, Roles.DISTRIBUTOR_CONTENT_ADMIN].some(
              (r) => r === localStorage.role
            ) && (
              <form
                action={`${process.env.REACT_APP_STUDENT_API_URL}/public/tasting`}
                target="_blank"
                method="post"
                style={{ display: 'inline-block' }}
              >
                <input type="hidden" name="unit" value={row.id} />
                <input type="hidden" name="type" value={props.academicPlan} />
                <input type="hidden" name="locale" value="pt-br" />
                <input type="hidden" name="disableInstructions" value="true" />
                <Button icon="select" buttonType="submit" tooltip="Tasting" />
              </form>
            )}{' '}
            {[Roles.ADMIN, Roles.SUPPORT, Roles.CONTENT_ADMIN, Roles.DISTRIBUTOR_CONTENT_ADMIN].some(
              (r) => r === localStorage.role
            ) && (
              <>
                {['DRAFT', 'UNPUBLISHED'].includes(row.status) &&
                  !!row.itemsCount &&
                  row.itemsWithImage === row.itemsThatNeedImage &&
                  row.itemsWithAudio === row.itemsThatNeedAudio &&
                  row.itemsWithVideo === row.itemsThatNeedVideo && (
                    <>
                      <Button
                        icon="cloud-upload"
                        buttonType="button"
                        onClick={() => props.onPublish(row)}
                        tooltip="Publish"
                      />{' '}
                    </>
                  )}
                {'PUBLISHED' === row.status && (
                  <>
                    <Button
                      icon="cloud-download"
                      buttonType="button"
                      onClick={() => props.onUnpublish(row)}
                      tooltip="Unpublish"
                    />{' '}
                  </>
                )}
                <Button icon="apartment" onClick={() => props.onDivide(row.module, row.id)} tooltip="Split" />
              </>
            )}
          </div>
        ),
      },
    ]}
    rows={props.units}
    selectable
    onSelect={(row) => {
      if (row.type.name.toLowerCase() === 'review') {
        browserHistory.push(`/modules/${row.module}/units/${row.id}/review-items`);
      } else {
        browserHistory.push(`/modules/${row.module}/units/${row.id}/items`);
      }
    }}
  />
);

UnitList.propTypes = {
  units: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  academicPlan: PropTypes.string.isRequired,
  fetching: PropTypes.bool.isRequired,
  allowReorder: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPublish: PropTypes.func.isRequired,
  onUnpublish: PropTypes.func.isRequired,
  onAutoReorder: PropTypes.func.isRequired,
  onDivide: PropTypes.func.isRequired,
};

export default UnitList;
